// src/components/QualitesGridNeon.tsx
import React, { JSX } from 'react';
import {
    Box,
    Grid,
    Card,
    CardContent,
    Typography,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';

// Import d'icônes spécifiques pour chaque qualité
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import FavoriteIcon from '@mui/icons-material/Favorite';
import SelfImprovementIcon from '@mui/icons-material/SelfImprovement';

// Données bilingues pour les qualités
interface Quality {
    title: {
        en: string;
        fr: string;
    };
    description: {
        en: string;
        fr: string;
    };
}

const qualities: Quality[] = [
    {
        title: { en: "Persevering", fr: "Persévérant" },
        description: {
            en: "Having overcome asthma, I committed to regular sports practice for years to overcome this condition.",
            fr: "Ancien asthmatique, j'ai dû m'engager dans une pratique sportive régulière pendant des années pour surmonter cette condition.",
        },
    },
    {
        title: { en: "Reliable", fr: "Fiable" },
        description: {
            en: "Known for my reliability, I was a trusted person during my experience at the Tour de France, where I secured the roads for eight hours.",
            fr: "Reconnu pour ma fiabilité, j'ai été une personne de confiance lors de mon expérience au Tour de France, où j'ai sécurisé les routes pendant huit heures.",
        },
    },
    {
        title: { en: "Caring", fr: "Attentionné" },
        description: {
            en: "I pay particular attention to the people I care about, valuing sincere and deep human relationships.",
            fr: "Je suis particulièrement attentionné envers les personnes que j'estime, valorisant les relations humaines sincères et profondes.",
        },
    },
    {
        title: { en: "Self-reliant", fr: "Autonome" },
        description: {
            en: "I intensely practice weight training without a coach by following my own program, demonstrating my self-reliance and ability to motivate myself.",
            fr: "Je pratique intensivement la musculation sans coach, en suivant mon propre programme, ce qui témoigne de mon autonomie et de ma capacité à me motiver.",
        },
    },
];

// Mapping des icônes en fonction du titre français (pour la correspondance)
const qualityIcons: Record<string, JSX.Element> = {
    "Persévérant": <FitnessCenterIcon sx={{ fontSize: 50, mb: 2, color: '#FF6200' }} />,
    "Fiable": <VerifiedUserIcon sx={{ fontSize: 50, mb: 2, color: '#FF6200' }} />,
    "Attentionné": <FavoriteIcon sx={{ fontSize: 50, mb: 2, color: '#FF6200' }} />,
    "Autonome": <SelfImprovementIcon sx={{ fontSize: 50, mb: 2, color: '#FF6200' }} />,
};

// Conteneur principal, responsive et adapté au mode
const Container = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.mode === 'dark'
        ? theme.palette.background.default
        : '#ffffff',
    color: theme.palette.text.primary,
    padding: theme.spacing(4),
    [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(2),
        margin: theme.spacing(2),
    },
    borderRadius: theme.shape.borderRadius,
    maxWidth: 1200,
    margin: '40px auto',
}));

// Titre de section, avec responsive
const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 'bold',
    marginBottom: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.primary,
    [theme.breakpoints.down('sm')]: {
        fontSize: '1.8rem',
    },
}));

// Carte de qualité, adaptative au mode sombre et responsive
const QualityCard = styled(Card)(({ theme }) => ({
    background: theme.palette.mode === 'dark'
        ? theme.palette.background.paper
        : 'linear-gradient(180deg, #fff 70%, #f9f9f9 100%)',
    color: theme.palette.text.primary,
    borderRadius: 8,
    boxShadow: theme.palette.mode === 'dark'
        ? '0 2px 8px rgba(255,255,255,0.1)'
        : '0 2px 8px rgba(0,0,0,0.1)',
    padding: theme.spacing(3),
    textAlign: 'center',
    transition: 'transform 0.3s ease',
    minHeight: 300,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    '&:hover': {
        transform: 'scale(1.02)',
    },
}));

interface QualitesGridNeonProps {
    language: 'en' | 'fr';
}

export default function QualitesGridNeon({ language }: QualitesGridNeonProps) {
    const theme = useTheme();

    // Texte pour le titre de section
    const texts = {
        en: { sectionTitle: "My Qualities" },
        fr: { sectionTitle: "Mes Qualités" },
    };

    return (
        <Container>
            <SectionTitle variant="h4">{texts[language].sectionTitle}</SectionTitle>
            <Grid container spacing={3}>
                {qualities.map((quality, idx) => (
                    <Grid item xs={12} sm={6} md={3} key={idx}>
                        <QualityCard>
                            <CardContent
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                }}
                            >
                                {/* Utilisation de l'icône basée sur le titre français */}
                                {qualityIcons[quality.title.fr] || null}
                                <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    {quality.title[language]}
                                </Typography>
                                <Typography variant="body2" sx={{ textAlign: 'center' }}>
                                    {quality.description[language]}
                                </Typography>
                            </CardContent>
                        </QualityCard>
                    </Grid>
                ))}
            </Grid>
        </Container>
    );
}
